import editor from './helpImages/editor.png';
import user_saved from './helpImages/user_saved.png';
import animation from './helpImages/animation.png';
import gradient from './helpImages/gradient.png';
import render from './helpImages/render.png';

export const helpMarkdown = `
# Fractal Explorer
Fractal Explorer allows exploration of a wide range of 2 and 3 dimensional fractals. It is primarily an interactive application that allows for fractals and their properties to be adjusted and previewed in real time. It can also produce high quality final renders and animations.

## Main Menu
The Main Menu allows for new fractals to be created, or saved or example fractals to be opened.

\`New Empty Fractal\` creates a fractal with an empty pipeline.

\`User Saved Fractals\` are those that have been previously saved locally (through localStorage).

![A fractal entry](${user_saved})

The left and right arrows on the fractal entry move the entry left or right. The trash icon deletes it. Selecting the entry name allows it to be renamed.

## Fractal Editor
<img src="${editor}" style="max-width: 100%"/>

### Movement Controls
In order to use keyboard movement controls, you must select the fractal render area first.
#### 2 Dimensional Fractals
- Mouse click and drag: pan
- Mouse scroll wheel: zoom (to/from mouse location)
- \`W\`/\`A\`/\`S\`/\`D\`: move up / left / down / right
- \`Q\`/\`E\`: zoom in / out
#### 3 Dimensional Fractals
- Mouse click and drag: rotate camera pitch / yaw
- Mouse scroll wheel: move in camera Z direction
- \`A\`/\`D\`: move in camera X direction (+/-)
- \`W\`/\`S\`: move in camera Y direction (+/-)
- \`Q\`/\`E\`: move in camera Z direction (+/-)
- \`J\`/\`L\`: rotate camera yaw (left/right)
- \`I\`/\`K\`: rotate camera pitch (up/down)
- \`U\`/\`O\`: rotate camera roll (clockwise / anti-clockwise)

### Pipeline
To edit the pipeline, select \`Pipeline\` in the top right. The pipeline defines the algorithms (shaders) to use for various components necessary to display the fractal. If any stages of the pipeline are not selected (they say "None"), a fractal will not be displayed.

Some shaders provide help information. They are indicated by a question mark icon next to the shader name. Selecting this icon displays popup help information.

### Properties
To edit fractal properties, select \`Properties\` in the top right. Each shader has its own section of properties.

The lock icon next to each property allows toggling between uniform (unlocked) and const (locked) properties. Adjustments to uniform properties take effect immediately. Adjustments to const properties may have a delay before they take effect. Uniform properties can result in lower rendering efficiency, but allow for more responsive adjustment.

#### Linear Gradients

![Linear gradient editor](${gradient})

A linear color gradient is defined by a number of color stops. Color is linearly interpolated between stops.

To add a new color stop to the gradient, click on the empty space directly below the gradient preview where you want the stop to be located.

To change a color stop in the gradient, select it and use the color editor below the gradient. To change a color stop's position, drag it. To delete a color stop, select it and click the 'X' icon below it.

#### Animation
To enable animation, select the \`Enable Animation Controls\` button at the bottom of the properties menu.

<img src="${animation}" style="max-width: 100%"/>

The animation editor is located below the fractal preview. The play/stop icon on the left allows the animation to be previewed in real time. The time indicator (the white bar) can be dragged to specific times to preview the animation at that point.

To add a new animation keyframe for a property at the current time, choose the diamond icon next to it in the Properties menu. To edit an existing keyframe, select it in the animation timeline.

Only uniform (non locked) properties can be animated. Some property types are always const (locked). Non-keyframed values are constant throughout the animation.

#### Rendering Modes
- \`Auto Compile\` compiles the pipeline whenever it changes. On older systems, this may take a substantial amount of time (up to ~10 seconds). If auto compile is turned off, you will have to choose \`Compile Shaders\` whenever the pipeline or const properties are changed.

- \`Progressive Rendering\` renders the fractal in smaller fragments, and combines them to produce a full render. The \`Tiles\` dropdown defines how many fragments the full render is split into. A higher tile count allows the fractal to be previewed at a higher frame rate (but initially lower resolution). Disabling progressive rendering can result in faster fractal rendering, but disables allow for anti-aliasing.

## Saving Fractals
#### Local Saving
The \`Save Fractal\` button in the upper bar allows a fractal to be saved to your browser's \`localStorage\`. Saved fractals should persist across browser restarts.

#### URL Saving
The \`Share as URL\` button in the upper bar allows a fractal to be saved and distributed as a URL. The URLs can be quite long (especially for animation). 

## Render Final Images / Video
To render a downloadable image or animation of a fractal, choose the \`Render\` button in the upper bar.

![Rendering parameters menu](${render})

- \`Resolution\` defines the resolution of the render. The menu on the right has a dropdown of common image dimensions. WebGL only supports rendering up to 4K, so higher resolutions require at least 4 tiles.
- \`Tiling\` defines the number of fragments to render the image in. Some graphics card drivers have a limit on the amount of time a program can run on the GPU (usually 2 seconds), so tiling may be required for high resolution or computationally expensive images.
- \`Samples Per Pixel\` defines the number of times to take a random sample in each pixel (anti-aliasing). A higher number will create cleaner images. Multiple samples are also required for motion blur.
- \`Enable Motion Blur\` simulates the blur created by a moving camera or object.
- \`Enable Animation\` renders multiple images at the defined framerate over the given time interval.
- \`Preview Update Interval\` is the frequency of the preview in the left area being updated. More frequent updates add rendering overhead.

Animations are downloaded as a zip file of images. An external tools, such as [ffmpeg](https://www.ffmpeg.org), can be used to produce a final video.
`;
